import { useNowTime } from "lib/hooks/useNowTime";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MarkdownView from "components/admin/contents/MarkdownView";
import { useTranslation } from "react-i18next";

import {
  getAdminNotices,
  createAdminNotice,
  updateAdminNotice,
  deleteAdminNotice,
  getAdminHelpTooltips,
  upsertAdminHelpTooltip,
  deleteAdminHelpTooltip,
} from "lib/api/adminContent";

const SUPPORTED_LOCALES = [
  { value: "ko-KR", label: "한국어" },
  { value: "en-US", label: "English" },
  { value: "vi-VN", label: "Tiếng Việt" },
  { value: "ja-JP", label: "日本語" },
  { value: "zh-CN", label: "中文" },
];

const makeEmptyNoticeTranslations = () => ({
  "ko-KR": { title: "", body: "" },
  "en-US": { title: "", body: "" },
  "ja-JP": { title: "", body: "" },
  "vi-VN": { title: "", body: "" },
  "zh-CN": { title: "", body: "" },
});

const makeEmptyHelpTranslations = () => ({
  "ko-KR": { title: "", content: "" },
  "en-US": { title: "", content: "" },
  "ja-JP": { title: "", content: "" },
  "vi-VN": { title: "", content: "" },
  "zh-CN": { title: "", content: "" },
});

const normalizeLocale = (value) => {
  const raw = String(value ?? "").toLowerCase();
  if (raw.startsWith("en")) return "en-US";
  if (raw.startsWith("ja")) return "ja-JP";
  if (raw.startsWith("vi")) return "vi-VN";
  if (raw.startsWith("zh")) return "zh-CN";
  return "ko-KR";
};

const buildNoticeTranslations = (item) => {
  const base = makeEmptyNoticeTranslations();
  const src = item?.translations ?? {};

  Object.entries(src).forEach(([locale, value]) => {
    const normalized = normalizeLocale(locale);
    base[normalized] = {
      title: String(value?.title ?? ""),
      body: String(value?.body ?? ""),
    };
  });

  if (!base["ko-KR"].title && item?.title) base["ko-KR"].title = String(item.title);
  if (!base["ko-KR"].body && item?.body) base["ko-KR"].body = String(item.body);

  return base;
};

const buildHelpTranslations = (item) => {
  const base = makeEmptyHelpTranslations();
  const src = item?.translations ?? {};

  Object.entries(src).forEach(([locale, value]) => {
    const normalized = normalizeLocale(locale);
    base[normalized] = {
      title: String(value?.title ?? ""),
      content: String(value?.content ?? ""),
    };
  });

  if (!base["ko-KR"].title && item?.title) base["ko-KR"].title = String(item.title);
  if (!base["ko-KR"].content && item?.content) {
    base["ko-KR"].content = String(item.content);
  }

  return base;
};

export default function AdminContentPage() {
  const { i18n } = useTranslation();
  const time = useNowTime();
  const [searchParams] = useSearchParams();

  const defaultTab = searchParams.get("tab") === "help" ? "help" : "notice";
  const [tab, setTab] = useState(defaultTab);

  const [notices, setNotices] = useState([]);
  const [tooltips, setTooltips] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const [q, setQ] = useState("");
  const [pubFilter, setPubFilter] = useState("all");

  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const currentLocale = normalizeLocale(i18n.resolvedLanguage || i18n.language);

  const loadContents = async () => {
    const n = await getAdminNotices(currentLocale);
    const t = await getAdminHelpTooltips(currentLocale);
    setNotices(n?.data?.notices ?? []);
    setTooltips(t?.data?.tooltips ?? []);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const n = await getAdminNotices(currentLocale);
      const t = await getAdminHelpTooltips(currentLocale);
      if (cancelled) return;

      setNotices(n?.data?.notices ?? []);
      setTooltips(t?.data?.tooltips ?? []);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentLocale]);

  const items = tab === "notice" ? notices : tooltips;

  const filteredItems = useMemo(() => {
    const keyword = q.trim().toLowerCase();

    return (items ?? [])
      .filter((x) => {
        const isPub = Number(x.is_published ?? 0) === 1;
        const pubOk =
          pubFilter === "all" || (pubFilter === "published" ? isPub : !isPub);

        const text =
          tab === "notice"
            ? `${x.title ?? ""} ${x.body ?? ""}`
            : `${x.key ?? ""} ${x.title ?? ""} ${x.content ?? ""}`;

        const hit = !keyword || text.toLowerCase().includes(keyword);
        return hit && pubOk;
      })
      .sort((a, b) => {
        const at = new Date(
          a.updated_at || a.published_at || a.created_at || 0,
        ).getTime();
        const bt = new Date(
          b.updated_at || b.published_at || b.created_at || 0,
        ).getTime();
        return bt - at;
      });
  }, [items, q, pubFilter, tab]);

  const selectedItem = useMemo(() => {
    if (!selectedKey) return null;
    if (tab === "notice") {
      return notices.find((n) => n.id === selectedKey) ?? null;
    }
    return tooltips.find((t) => t.key === selectedKey) ?? null;
  }, [selectedKey, tab, notices, tooltips]);

  const reload = async () => {
    await loadContents();
  };

  const onCreate = () => {
    setEditing(null);
    setOpenEditor(true);
  };

  const onEdit = () => {
    if (!selectedItem) return;
    setEditing(selectedItem);
    setOpenEditor(true);
  };

  const onDelete = async () => {
    if (!selectedItem) return;
    if (!window.confirm("삭제할까요?")) return;

    if (tab === "notice") {
      await deleteAdminNotice(selectedItem.id);
    } else {
      await deleteAdminHelpTooltip(selectedItem.key);
    }

    setSelectedKey(null);
    await reload();
  };

  const onSave = async (payload) => {
    if (tab === "notice") {
      if (editing?.id) await updateAdminNotice(editing.id, payload);
      else await createAdminNotice(payload);
    } else {
      await upsertAdminHelpTooltip(payload);
    }

    setOpenEditor(false);
    setEditing(null);
    await reload();
  };

  return (
    <section className="content admin roleAdminPage opsContent adminContentPage">
      <div className="roleAdminHeader">
        <div>
          <h2>공지사항·도움말 관리</h2>
          <p>{time}</p>
        </div>
        <button
          className="roleAdminPrimaryButton"
          onClick={onCreate}
          type="button"
        >
          {tab === "notice" ? "공지 작성" : "도움말 작성"}
        </button>
      </div>

      <div className="tableTools">
        <div className="seg">
          <button
            className={`segBtn ${tab === "notice" ? "on" : ""}`}
            onClick={() => {
              setTab("notice");
              setSelectedKey(null);
            }}
          >
            공지사항
          </button>
          <button
            className={`segBtn ${tab === "help" ? "on" : ""}`}
            onClick={() => {
              setTab("help");
              setSelectedKey(null);
            }}
          >
            도움말
          </button>
        </div>

        <input
          className="searchInput"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="제목·내용·도움말 식별자 검색"
        />

        <select
          className="select"
          value={pubFilter}
          onChange={(e) => setPubFilter(e.target.value)}
        >
          <option value="all">전체</option>
          <option value="published">게시</option>
          <option value="draft">초안/비공개</option>
        </select>
      </div>

      <div className="mainGrid">
        <AdminContentTableCard
          tab={tab}
          items={filteredItems}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
        />

        <AdminContentDetailCard
          tab={tab}
          item={selectedItem}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>

      <AdminContentEditorModal
        open={openEditor}
        tab={tab}
        currentLocale={currentLocale}
        initial={editing}
        onClose={() => {
          setOpenEditor(false);
          setEditing(null);
        }}
        onSave={onSave}
      />
    </section>
  );
}

function AdminContentTableCard({ tab, items, selectedKey, onSelect }) {
  return (
    <div className="card opsTableCard">
      <div className="cardHeader">
        <h3>{tab === "notice" ? "공지 목록" : "도움말 목록"}</h3>
      </div>

      <div className="tableWrap">
        <table className="opsTable">
          <thead>
            <tr>
              <th>{tab === "notice" ? "제목" : "도움말 식별자"}</th>
              <th>상태</th>
              <th>{tab === "notice" ? "게시일" : "수정일"}</th>
            </tr>
          </thead>
          <tbody>
            {(items ?? []).length === 0 ? (
              <tr>
                <td className="empty" colSpan={3}>
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              (items ?? []).map((x) => {
                const key = tab === "notice" ? x.id : x.key;
                const isSel = key === selectedKey;
                const isPub = Number(x.is_published ?? 0) === 1;

                return (
                  <tr
                    key={key}
                    className={isSel ? "active" : ""}
                    onClick={() => onSelect(key)}
                  >
                    <td className="ellipsis">
                      {tab === "notice" ? (
                        <b>{x.title}</b>
                      ) : (
                        <code>{x.key}</code>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${isPub ? "pub" : "draft"}`}>
                        {isPub ? "게시" : tab === "notice" ? "초안" : "비공개"}
                      </span>
                    </td>
                    <td>
                      {tab === "notice"
                        ? (x.published_at ?? "-")
                        : (x.updated_at ?? "-")}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminContentDetailCard({ tab, item, onEdit, onDelete }) {
  return (
    <div className="card opsDetailCard">
      <div className="cardHeader">
        <h3>상세</h3>
        <div className="btnRow">
          <button className="btn" onClick={onEdit} disabled={!item}>
            수정
          </button>
          <button className="btn danger" onClick={onDelete} disabled={!item}>
            삭제
          </button>
        </div>
      </div>

      {!item ? (
        <div className="emptyPanel">좌측 목록에서 항목을 선택하세요.</div>
      ) : (
        <div>
          <div className="metaRow">
            <span
              className={`badge ${Number(item.is_published ?? 0) ? "pub" : "draft"}`}
            >
              {Number(item.is_published ?? 0)
                ? "게시"
                : tab === "notice"
                  ? "초안"
                  : "비공개"}
            </span>
            {tab === "notice" && Number(item.pinned ?? 0) ? (
              <span className="badge">고정</span>
            ) : null}
          </div>

          <h4 className="title">
            {tab === "notice" ? item.title : (item.title ?? item.key)}
          </h4>
          <p className="sub">
            {tab === "notice"
              ? `게시일: ${item.published_at ?? "-"} / 수정일: ${item.updated_at ?? "-"}`
              : `수정일: ${item.updated_at ?? "-"}`}
          </p>

          <div className="divider" />

          <div className="markdown">
            <MarkdownView value={tab === "notice" ? item.body : item.content} />
          </div>
        </div>
      )}
    </div>
  );
}

function AdminContentEditorModal({
  open,
  tab,
  currentLocale,
  initial,
  onClose,
  onSave,
}) {
  const [preview, setPreview] = useState(false);
  const [activeLocale, setActiveLocale] = useState("ko-KR");

  const [noticeTranslations, setNoticeTranslations] = useState(
    buildNoticeTranslations(initial),
  );
  const [isPublished, setIsPublished] = useState(
    Number(initial?.is_published ?? 0),
  );
  const [pinned, setPinned] = useState(Number(initial?.pinned ?? 0));

  const [key, setKey] = useState(initial?.key ?? "");
  const [helpTranslations, setHelpTranslations] = useState(
    buildHelpTranslations(initial),
  );

  useEffect(() => {
    if (!open) return;
    setPreview(false);
    setActiveLocale(normalizeLocale(currentLocale));

    setNoticeTranslations(buildNoticeTranslations(initial));
    setIsPublished(Number(initial?.is_published ?? 0));
    setPinned(Number(initial?.pinned ?? 0));

    setKey(initial?.key ?? "");
    setHelpTranslations(buildHelpTranslations(initial));
  }, [open, initial, currentLocale]);

  if (!open) return null;

  const localeNotice = noticeTranslations[activeLocale] ?? { title: "", body: "" };
  const localeHelp = helpTranslations[activeLocale] ?? { title: "", content: "" };

  const updateNoticeTranslation = (field, value) => {
    setNoticeTranslations((prev) => ({
      ...prev,
      [activeLocale]: {
        ...(prev[activeLocale] ?? { title: "", body: "" }),
        [field]: value,
      },
    }));
  };

  const updateHelpTranslation = (field, value) => {
    setHelpTranslations((prev) => ({
      ...prev,
      [activeLocale]: {
        ...(prev[activeLocale] ?? { title: "", content: "" }),
        [field]: value,
      },
    }));
  };

  const submit = () => {
    const noticeKo = noticeTranslations["ko-KR"] ?? { title: "", body: "" };
    const helpKo = helpTranslations["ko-KR"] ?? { title: "", content: "" };

    if (tab === "notice") {
      onSave({
        title: noticeKo.title,
        body: noticeKo.body,
        is_published: isPublished,
        pinned,
        translations: noticeTranslations,
      });
    } else {
      onSave({
        key,
        title: helpKo.title,
        content: helpKo.content,
        is_published: isPublished,
        translations: helpTranslations,
      });
    }
  };

  return (
    <div className="contentModal">
      <div className="adminModal">
        <div className="modalHeader">
          <div className="titleBox">
            <h3>{initial ? "수정" : "작성"}</h3>
          </div>

          <button className="iconBtn" onClick={onClose}>
            X
          </button>
        </div>

        <div className="field localeField">
          <p className="label">언어</p>
          <div className="editTabs localeTabs">
            {SUPPORTED_LOCALES.map((item) => (
              <button
                key={item.value}
                className={`miniBtn ${activeLocale === item.value ? "on" : ""}`}
                onClick={() => setActiveLocale(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="editTabs">
          <button
            className={`miniBtn ${!preview ? "on" : ""}`}
            onClick={() => setPreview(false)}
          >
            편집
          </button>
          <button
            className={`miniBtn ${preview ? "on" : ""}`}
            onClick={() => setPreview(true)}
          >
            미리보기
          </button>
        </div>

        {tab === "notice" ? (
          <>
            <div className="field">
              <p className="label">제목 ({activeLocale})</p>
              <input
                className="input"
                value={localeNotice.title}
                onChange={(e) => updateNoticeTranslation("title", e.target.value)}
              />
            </div>

            <div className="field">
              <p className="label">내용 ({activeLocale})</p>
              {!preview ? (
                <textarea
                  className="textarea"
                  value={localeNotice.body}
                  onChange={(e) => updateNoticeTranslation("body", e.target.value)}
                />
              ) : (
                <div className="previewBox">
                  <MarkdownView value={localeNotice.body} />
                </div>
              )}
            </div>

            <div className="field">
              <div className="checkRow">
                <label>
                  <input
                    type="checkbox"
                    checked={!!isPublished}
                    onChange={(e) => setIsPublished(e.target.checked ? 1 : 0)}
                  />
                  게시
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={!!pinned}
                    onChange={(e) => setPinned(e.target.checked ? 1 : 0)}
                  />
                  상단 고정
                </label>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <p className="label">도움말 식별자</p>
              <input
                className="input"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                disabled={!!initial?.key}
              />
            </div>

            <div className="field">
              <p className="label">제목 ({activeLocale})</p>
              <input
                className="input"
                value={localeHelp.title}
                onChange={(e) => updateHelpTranslation("title", e.target.value)}
              />
            </div>

            <div className="field">
              <p className="label">내용 (마크다운 형식, {activeLocale})</p>
              {!preview ? (
                <textarea
                  className="textarea"
                  value={localeHelp.content}
                  onChange={(e) => updateHelpTranslation("content", e.target.value)}
                />
              ) : (
                <div className="previewBox">
                  <MarkdownView value={localeHelp.content} />
                </div>
              )}
            </div>

            <div className="field">
              <div className="checkRow">
                <label>
                  <input
                    type="checkbox"
                    checked={!!isPublished}
                    onChange={(e) => setIsPublished(e.target.checked ? 1 : 0)}
                  />
                  게시
                </label>
              </div>
            </div>
          </>
        )}

        <div className="modalFooter">
          <button className="ghostBtn" onClick={onClose}>
            취소
          </button>
          <button className="primaryBtn" onClick={submit}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
